// Enemy stat sheets. Behavior code lives in src/game/enemies/*.

export const ENEMY_STATS = {
  drone: {
    hp: 1,
    moveSpeed: 2.8,
    turnSpeed: 5,
    sightRange: 18,
    attackRange: 12,
    fireCooldown: 1.3,
    projectileSpeed: 14,
    projectileDamage: 1,
    bodyColor: 0x334255,
    accentColor: 0xff4060,
    radius: 0.55,
    height: 1.5,
    viewConeAngleDeg: 75,
    alertMemory: 2.2,
    roamRadius: 5,
    roamSpeedMul: 0.58,
    damageTakenMul: 1,
  },
  enforcer: {
    hp: 5,
    moveSpeed: 3.6,
    turnSpeed: 6,
    sightRange: 20,
    attackRange: 9,
    fireCooldown: 1.6,
    burstCount: 3,
    burstInterval: 0.09,
    projectileSpeed: 16,
    projectileDamage: 1,
    spread: 0.18,
    bodyColor: 0x3a3048,
    accentColor: 0xffaa33,
    radius: 0.7,
    height: 1.8,
    viewConeAngleDeg: 68,
    alertMemory: 2.4,
    roamRadius: 4.8,
    roamSpeedMul: 0.5,
    damageTakenMul: 0.78,
  },
  zealot: {
    hp: 1,
    moveSpeed: 5.4,
    turnSpeed: 8,
    sightRange: 22,
    attackRange: 1.6,
    fireCooldown: 0.8,
    meleeWindup: 0.35,
    meleeDamage: 2,
    bodyColor: 0x4a1a22,
    accentColor: 0xff3080,
    radius: 0.55,
    height: 1.7,
    viewConeAngleDeg: 90,
    alertMemory: 2.8,
    roamRadius: 6,
    roamSpeedMul: 0.7,
    damageTakenMul: 1,
  },
  minmon: {
    hp: 1,
    moveSpeed: 6.1,
    turnSpeed: 9,
    sightRange: 20,
    attackRange: 1.25,
    fireCooldown: 0.55,
    meleeDamage: 1,
    bodyColor: 0x263126,
    accentColor: 0x91ff68,
    radius: 0.42,
    height: 1.2,
    viewConeAngleDeg: 105,
    alertMemory: 3.2,
    roamRadius: 7.5,
    roamSpeedMul: 0.85,
    damageTakenMul: 1.08,
  },
  zilua: {
    hp: 3,
    moveSpeed: 4.1,
    turnSpeed: 7,
    sightRange: 24,
    attackRange: 13,
    fireCooldown: 1.25,
    projectileSpeed: 13.5,
    projectileDamage: 1,
    summonCooldown: 6.4,
    summonBatchSize: 2,
    summonMaxActive: 5,
    summonMinRadius: 1.2,
    summonMaxRadius: 3.1,
    bodyColor: 0x2f2a45,
    accentColor: 0xff68d9,
    radius: 0.58,
    height: 1.65,
    viewConeAngleDeg: 88,
    alertMemory: 3.1,
    roamRadius: 6.4,
    roamSpeedMul: 0.72,
    damageTakenMul: 1.05,
  },
};

/**
 * First-encounter tutorial card definitions keyed by enemy type id.
 * IDs must match runtime/spawner enemy type identifiers.
 */
export const ENEMY_TUTORIAL_DATA = {
  drone: {
    id: 'drone',
    displayName: 'Drone',
    threatLevel: 1,
    abilities: [
      { icon: 'fast_move', label: 'Fast Movement', desc: 'Moves quickly and unpredictably.' },
      { icon: 'ranged', label: 'Ranged Fire', desc: 'Fires in bursts from a distance.' },
    ],
    tip: 'Stay mobile. Strafe constantly to avoid its burst pattern.',
    cardImage: 'public/assets/icons/enemies/drone.png',
  },
  enforcer: {
    id: 'enforcer',
    displayName: 'Enforcer',
    threatLevel: 2,
    abilities: [
      { icon: 'burst_fire', label: 'Burst Fire', desc: 'Shoots short, rapid volleys with spread.' },
      { icon: 'combat_strafe', label: 'Combat Strafe', desc: 'Maintains pressure while circling your position.' },
    ],
    tip: 'Break line-of-sight between bursts and punish reload windows.',
    cardImage: 'public/assets/icons/enemies/enforcer.png',
  },
  zealot: {
    id: 'zealot',
    displayName: 'Zealot',
    threatLevel: 2,
    abilities: [
      { icon: 'melee_dash', label: 'Melee Rush', desc: 'Closes distance quickly to force close combat.' },
      { icon: 'windup', label: 'Windup Strike', desc: 'Telegraphs a heavy baton swing before impact.' },
    ],
    tip: 'Backstep during the windup, then counter while it recovers.',
    cardImage: 'public/assets/icons/enemies/zealot.png',
  },
  minmon: {
    id: 'minmon',
    displayName: 'Minmon',
    threatLevel: 1,
    abilities: [
      { icon: 'swarm', label: 'Swarm Pressure', desc: 'Rushes aggressively and surrounds targets.' },
      { icon: 'self_destruct', label: 'Self-Destruct', desc: 'Arms and detonates if it gets too close.' },
    ],
    tip: 'Prioritize these first. Never let a cluster arm near you.',
    cardImage: 'public/assets/icons/enemies/minmon.png',
  },
  zilua: {
    id: 'zilua',
    displayName: 'Zilua',
    threatLevel: 3,
    abilities: [
      { icon: 'summon', label: 'Summoner', desc: 'Calls additional Minmons into the fight.' },
      { icon: 'ranged_control', label: 'Ranged Control', desc: 'Kites while firing accurate energy shots.' },
    ],
    tip: 'Collapse on Zilua early or the battlefield snowballs out of control.',
    cardImage: 'public/assets/icons/enemies/zilua.png',
  },
  boss: {
    id: 'boss',
    displayName: 'Nihil Warden',
    threatLevel: 3,
    abilities: [
      { icon: 'pattern_barrage', label: 'Pattern Barrage', desc: 'Cycles fan, spiral, and burst shot patterns.' },
      { icon: 'heavy_slash', label: 'Heavy Slash', desc: 'Punishes close range with a high-damage melee strike.' },
    ],
    tip: 'Read telegraphs, keep medium distance, and avoid overcommitting.',
    cardImage: 'public/assets/icons/enemies/boss.png',
  },
  boss_phase2: {
    id: 'boss_phase2',
    displayName: 'Nihil Warden - Phase 2',
    threatLevel: 3,
    abilities: [
      { icon: 'summon_waves', label: 'Summon Waves', desc: 'Calls elite reinforcements during the phase shift.' },
      { icon: 'enrage', label: 'Enrage State', desc: 'Becomes more aggressive at low health.' },
    ],
    tip: 'Clear summons fast and reset positioning before the boss returns.',
    cardImage: 'public/assets/icons/enemies/boss_phase2.png',
  },
};

export const ENEMY_POINT_COSTS = Object.freeze({
  drone: 1,
  minmon: 1,
  enforcer: 2,
  zealot: 2,
  zilua: 3,
});

export const ROOM_SPAWN_TABLE = Object.freeze({
  1: Object.freeze({
    budget: 3,
    waves: [['drone', 'drone', 'drone']],
  }),
  2: Object.freeze({
    budget: 4,
    waves: [['drone', 'drone', 'drone'], ['zealot']],
  }),
  3: Object.freeze({
    budget: 5,
    waves: [['drone', 'drone'], ['enforcer'], ['zealot']],
  }),
  4: Object.freeze({
    budget: 6,
    waves: [['drone', 'drone'], ['enforcer'], ['zilua']],
  }),
  5: Object.freeze({
    miniBoss: true,
    waves: [['boss_mini'], ['drone'], ['drone']],
  }),
  6: Object.freeze({
    budget: 7,
    waves: [['enforcer'], ['zealot'], ['zealot'], ['drone']],
  }),
  7: Object.freeze({
    budget: 8,
    waves: [['enforcer'], ['enforcer'], ['zealot'], ['minmon'], ['drone']],
  }),
  8: Object.freeze({
    budget: 9,
    waves: [['zealot'], ['zealot'], ['zilua'], ['enforcer'], ['drone']],
  }),
  9: Object.freeze({
    budget: 10,
    waves: [['zealot'], ['zealot'], ['zilua'], ['zilua'], ['enforcer']],
  }),
  10: Object.freeze({
    finalBoss: true,
    phase2Adds: ['drone', 'drone'],
    waves: [['boss_final']],
  }),
});

export const ROOM_PRESSURE_TABLE = {
  1: {
    killQuota: 6,
    spawnIntervalStart: 3.0,
    spawnIntervalMin: 2.0,
    intervalDecayRate: 0.05,
    escalation: [
      { afterSeconds: 0,  allowedTypes: ["zealot"] },
    ]
  },
  2: {
    killQuota: 8,
    spawnIntervalStart: 2.5,
    spawnIntervalMin: 1.5,
    intervalDecayRate: 0.06,
    escalation: [
      { afterSeconds: 0,  allowedTypes: ["zealot"] },
      { afterSeconds: 15, allowedTypes: ["zealot"] },
    ]
  },
  3: {
    killQuota: 10,
    spawnIntervalStart: 2.0,
    spawnIntervalMin: 1.2,
    intervalDecayRate: 0.07,
    escalation: [
      { afterSeconds: 0,  allowedTypes: ["zealot"] },
      { afterSeconds: 15, allowedTypes: ["zealot"] },
      { afterSeconds: 30, allowedTypes: ["zealot", "enforcer"] },
    ]
  },
  4: {
    killQuota: 12,
    spawnIntervalStart: 2.0,
    spawnIntervalMin: 1.0,
    intervalDecayRate: 0.08,
    escalation: [
      { afterSeconds: 0,  allowedTypes: ["zealot"] },
      { afterSeconds: 15, allowedTypes: ["zealot"] },
      { afterSeconds: 30, allowedTypes: ["zealot", "zilua"] },
    ]
  },
  5: { isBoss: true, bossType: "miniboss", addSpawns: [] },
  6: {
    killQuota: 14,
    spawnIntervalStart: 1.8,
    spawnIntervalMin: 0.9,
    intervalDecayRate: 0.09,
    escalation: [
      { afterSeconds: 0,  allowedTypes: ["zealot"] },
      { afterSeconds: 15, allowedTypes: ["zealot", "enforcer"] },
      { afterSeconds: 30, allowedTypes: ["zealot", "enforcer", "zilua"] },
    ]
  },
  7: {
    killQuota: 16,
    spawnIntervalStart: 1.5,
    spawnIntervalMin: 0.8,
    intervalDecayRate: 0.10,
    escalation: [
      { afterSeconds: 0,  allowedTypes: ["zealot"] },
      { afterSeconds: 15, allowedTypes: ["zealot", "enforcer"] },
      { afterSeconds: 30, allowedTypes: ["zealot", "enforcer", "zilua", "minmon"] },
    ]
  },
  8: {
    killQuota: 18,
    spawnIntervalStart: 1.5,
    spawnIntervalMin: 0.7,
    intervalDecayRate: 0.11,
    escalation: [
      { afterSeconds: 0,  allowedTypes: ["zealot", "enforcer"] },
      { afterSeconds: 15, allowedTypes: ["zealot", "enforcer", "zilua"] },
      { afterSeconds: 30, allowedTypes: ["enforcer", "zilua", "minmon"] },
    ]
  },
  9: {
    killQuota: 20,
    spawnIntervalStart: 1.2,
    spawnIntervalMin: 0.6,
    intervalDecayRate: 0.12,
    escalation: [
      { afterSeconds: 0,  allowedTypes: ["zealot", "enforcer"] },
      { afterSeconds: 15, allowedTypes: ["zealot", "enforcer", "zilua"] },
      { afterSeconds: 30, allowedTypes: ["enforcer", "zilua", "minmon", "zealot"] },
    ]
  },
  10: {
    isBoss: true,
    bossType: "finalboss",
    addSpawns: [
      { afterPhase: 2, spawnType: "zealot", intervalSeconds: 4.0 }
    ]
  },
};


